import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with settings for large file uploads (2-4 hour audio files)
const api = axios.create({
  baseURL: API,
  timeout: 600000, // 10 minutes timeout for large files
  maxContentLength: 5 * 1024 * 1024 * 1024, // 5GB
  maxBodyLength: 5 * 1024 * 1024 * 1024, // 5GB
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  login: (username, password) => api.post('/admin/login', { username, password }),
  verify: () => api.post('/admin/verify'),
};

// Surah API
export const surahAPI = {
  getAll: () => api.get('/surahs'),
  getOne: (id) => api.get(`/surahs/${id}`),
  create: (data) => api.post('/admin/surahs', data),
  update: (id, data) => api.put(`/admin/surahs/${id}`, data),
  delete: (id) => api.delete(`/admin/surahs/${id}`),
  bulkDelete: (ids) => api.post('/admin/surahs/bulk-delete', ids),
};

// Verse API
export const verseAPI = {
  getBySurah: (surahId) => api.get(`/verses/${surahId}`),
  create: (data) => api.post('/admin/verses', data),
  update: (id, data) => api.put(`/admin/verses/${id}`, data),
  delete: (id) => api.delete(`/admin/verses/${id}`),
  bulkDelete: (ids) => api.post('/admin/verses/bulk-delete', ids),
};

// Meal API
export const mealAPI = {
  getAll: () => api.get('/meals'),
  getOne: (id) => api.get(`/meals/${id}`),
  create: (formData, onProgress) => api.post('/admin/meals', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      }
    }
  }),
  update: (id, formData, onProgress) => api.put(`/admin/meals/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      }
    }
  }),
  delete: (id) => api.delete(`/admin/meals/${id}`),
};

// Reciter API
export const reciterAPI = {
  getAll: () => api.get('/reciters'),
  getOne: (id) => api.get(`/reciters/${id}`),
  create: (formData) => api.post('/admin/reciters', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, formData) => api.put(`/admin/reciters/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/admin/reciters/${id}`),
};

// Recitation API (supports large audio files: mp3, mp4, wav, etc.)
export const recitationAPI = {
  getByReciter: (reciterId) => api.get(`/recitations/reciter/${reciterId}`),
  getBySurah: (surahId) => api.get(`/recitations/surah/${surahId}`),
  create: (formData, onProgress) => api.post('/admin/recitations', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 1800000, // 30 minutes for very large files (2-4 hours audio)
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      }
    }
  }),
  update: (id, formData, onProgress) => api.put(`/admin/recitations/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 1800000, // 30 minutes for very large files
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      }
    }
  }),
  delete: (id) => api.delete(`/admin/recitations/${id}`),
};

// Site Settings API
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (formData) => api.put('/admin/settings', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export default api;